import React, { useRef } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { DELIVERY_SERIES_COLOR, STATE_GREY, fmtStamp, timeAxis, toChartDate, withAlpha } from '../lib/chartTheme';
import { activatedSpans, stateSpans, toRows } from '../lib/deliveryChartRows';
import { committedProduct } from '../lib/marketReportsV1';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// Keep CanvasJS snappy even if a window returns a lot of points (7 d × 96 = 672 normally).
const MAX_POINTS = 3000;

const downsample = rows => {
    if (rows.length <= MAX_POINTS) {
        return rows;
    }
    const step = Math.ceil(rows.length / MAX_POINTS);
    return rows.filter((_, i) => i % step === 0);
};

// The activated wash: a light violet behind the periods the FCR service was actually
// activated in, so a glance says when the battery had to respond.
const ACTIVATED_ALPHA = 0.14;

// The non-measured states, as two alphas of the one grey: out of service is the lighter wash,
// unmeasured (a hole in the record rather than a state the battery was in) the darker one.
const STATE_ALPHA = { out_of_service: 0.16, unmeasured: 0.34 };

export const SERIES = {
    committed: 'Committed',
    availMin: 'Available capacity',
    score: 'Setpoint following score',
};

/**
 * The strip lines CanvasJS draws behind the series: the activated wash (the frequency left
 * this market's dead band, so the battery had to respond) and the state shadings (out of
 * service / unmeasured). The two never overlap — a non-measured period is never activated.
 * Exported for the tests: jsdom has no canvas, so the chart itself is not rendered there.
 *
 * @param {import('../lib/deliveryChartRows').DeliveryChartRow[]} rows
 */
export const stripLines = rows => [
    ...activatedSpans(rows).map(span => ({
        startValue: toChartDate(span.from),
        endValue: toChartDate(span.to),
        color: withAlpha(DELIVERY_SERIES_COLOR.activated, ACTIVATED_ALPHA),
    })),
    ...stateSpans(rows).map(span => ({
        startValue: toChartDate(span.from),
        endValue: toChartDate(span.to),
        color: withAlpha(STATE_GREY, STATE_ALPHA[span.state]),
    })),
];

const point = (row, y) => ({ x: toChartDate(row.t), y, t: row.t });

/**
 * The CanvasJS series for the rows — the three curves of one market's delivery report:
 * committed capacity (kW, the market's own scheduled power when the rows were built per
 * market), the minimum available capacity of each period (kW), and the setpoint following
 * score (%, right axis, over every measured period). Every point carries the true instant in
 * `t` so the tooltip can stamp it in UTC.
 *
 * @param {import('../lib/deliveryChartRows').DeliveryChartRow[]} rows
 * @param {{label: string, color: string}|null} product
 */
export const buildSeries = (rows, product) => [
    {
        type: 'stepLine',
        name: product ? `${SERIES.committed} · ${product.label}` : SERIES.committed,
        color: product ? product.color : STATE_GREY,
        lineThickness: 1.8,
        lineDashType: 'dash',
        markerSize: 0,
        showInLegend: true,
        xValueType: 'dateTime',
        toolTipContent: '{name}: {y} kW',
        dataPoints: rows.map(row => point(row, row.committed)),
    },
    {
        type: 'line',
        name: SERIES.availMin,
        color: DELIVERY_SERIES_COLOR.availMean,
        lineThickness: 1.8,
        markerSize: 3,
        markerType: 'circle',
        showInLegend: true,
        xValueType: 'dateTime',
        toolTipContent: '{name}: {y} kW',
        dataPoints: rows.map(row => point(row, row.availMin)),
    },
    {
        type: 'line',
        name: SERIES.score,
        color: DELIVERY_SERIES_COLOR.score,
        axisYType: 'secondary',
        lineThickness: 1.2,
        markerSize: 3,
        markerType: 'circle',
        showInLegend: true,
        xValueType: 'dateTime',
        toolTipContent: '{name}: {y} %',
        dataPoints: rows.map(row => point(row, row.score)),
    },
];

/**
 * Delivery report chart for the 24 h / 7 d windows — one market's committed capacity against
 * the minimum capacity that was actually backed by a reachable device, and how closely the
 * battery followed its power setpoint (the setpoint following score, every measured period).
 *
 * With a `market`, the committed curve is that market's own scheduled power — the split view,
 * one chart per market — and the activated wash marks the periods that market's frequency
 * left its dead band in. Without one it falls back to the period's single `committedPower`.
 * Availability and the score are battery-wide in the settled record and are the same on
 * every market's chart.
 *
 * Commitment and availability share the left kW axis: both are MW on the wire and the
 * comparison between them is the whole point. The score is a rate and gets the right axis.
 * Behind everything, the activated wash and the two state shadings. The x axis spans the
 * requested window, not the data extent, in UTC.
 *
 * Legend clicks toggle series. Toggles are kept in a ref so they survive the options being
 * rebuilt on every render.
 */
const DeliveryChart = ({ reports, from, to, market = null }) => {
    const visibilityRef = useRef({});

    const toggleDataSeries = e => {
        const visible = !(typeof e.dataSeries.visible === 'undefined' || e.dataSeries.visible);
        e.dataSeries.visible = visible;
        visibilityRef.current[e.dataSeries.name] = visible;
        e.chart.render();
    };

    const rows = downsample(toRows(reports, market));
    const product = market ?? committedProduct(reports);
    const series = buildSeries(rows, product);
    series.forEach(s => {
        if (visibilityRef.current[s.name] !== undefined) {
            s.visible = visibilityRef.current[s.name];
        }
    });

    const options = {
        height: 300,
        zoomEnabled: true,
        animationEnabled: false,
        axisX: {
            ...timeAxis(Date.parse(from), Date.parse(to)),
            lineThickness: 1,
            stripLines: stripLines(rows),
        },
        axisY: { title: 'kW', minimum: 0, lineThickness: 1, gridThickness: 1, gridColor: '#eef0f2' },
        axisY2: { title: '%', minimum: 0, maximum: 100, interval: 50, lineThickness: 1, gridThickness: 0 },
        legend: { cursor: 'pointer', itemclick: toggleDataSeries, fontSize: 12 },
        toolTip: {
            shared: true,
            contentFormatter: e => {
                const t = e.entries[0]?.dataPoint?.t;
                const lines = e.entries
                    .filter(entry => entry.dataPoint.y !== null && entry.dataPoint.y !== undefined)
                    .map(entry => {
                        const y = entry.dataPoint.y;
                        const value = Array.isArray(y)
                            ? `${y[0].toFixed(1)}–${y[1].toFixed(1)} kW`
                            : entry.dataSeries.name === SERIES.score
                            ? `${y.toFixed(1)} %`
                            : `${y.toFixed(1)} kW`;
                        return `<span style="color:${entry.dataSeries.color}">${entry.dataSeries.name}: ${value}</span>`;
                    });
                return `<div style="font-size:12px"><div style="color:#7e8389">${t ? fmtStamp(t) : ''}</div>${lines.join('<br/>')}</div>`;
            },
        },
        data: series,
    };

    return <CanvasJSChart options={options} />;
};

export default React.memo(DeliveryChart);
